import { gql, useLazyQuery, useMutation } from '@apollo/client';
import Modal from '@orn/shared-web/components/Modal/Modal';
import { cx } from '@orn/shared-web/utils/cx';
import formatInstantInTimeZone, { PACIFIC_TIME_ZONE } from '@orn/shared-web/utils/formatInstantInTimeZone';
import formatTimeAgo from '@orn/shared-web/utils/formatTimeAgo';
import * as React from 'react';
import { useSearchParams } from 'react-router-dom';

import AdminSelectUser, { type IAdminSelectUserSelection } from '../../components/admin/AdminSelectUser';
import Button from '../../components/Button';
import Input from '../../components/Input';
import { useToast } from '../../components/Toast';
import { useAuthState } from '../../providers/AuthProvider';

import { firstLineOfSummary } from './compactionSummaryPreview';
import { prefixReason } from './prefixReason';
import { groupMessagesByTurn, type FeedbackRow } from './transcript/transcriptModel';
import { TurnGroup } from './transcript/TranscriptView';

import type {
  AdminDeleteAllAssistantSessionsForUserMutation,
  AdminDeleteAllAssistantSessionsForUserMutationVariables,
  AdminDeleteAssistantSessionMutation,
  AdminDeleteAssistantSessionMutationVariables,
  AdminGetAssistantSessionDetailQuery,
  AdminGetAssistantSessionDetailQueryVariables,
  AdminListAssistantFeedbackForThreadQuery,
  AdminListAssistantFeedbackForThreadQueryVariables,
  AdminListAssistantSessionsForUserQuery,
  AdminListAssistantSessionsForUserQueryVariables,
  AdminRecompactAssistantSessionMutation,
  AdminRecompactAssistantSessionMutationVariables,
  AdminReExtractAssistantSessionMemoryMutation,
  AdminReExtractAssistantSessionMemoryMutationVariables,
} from './__generated__/AssistantSessionsScene.gql';

const ADMIN_LIST_SESSIONS = gql`
  query AdminListAssistantSessionsForUser($userId: ID!) {
    adminListAssistantSessionsForUser(userId: $userId) {
      hasMore
      sessions {
        id
        userId
        familyId
        channel
        externalThreadKey
        status
        priorThreadId
        lastActivityAt
        createdAt
        updatedAt
        messageCount
      }
    }
  }
`;

const ADMIN_GET_SESSION_DETAIL = gql`
  query AdminGetAssistantSessionDetail($threadId: ID!, $afterSequence: Int) {
    adminGetAssistantSessionDetail(threadId: $threadId, afterSequence: $afterSequence) {
      hasMore
      nextAfterSequence
      session {
        id
        userId
        familyId
        channel
        externalThreadKey
        status
        priorThreadId
        lastActivityAt
        createdAt
        updatedAt
        messageCount
        summary
        compactedAt
        compactionModelId
        compactionMetadata {
          keptCount
          summarizedCount
          droppedCount
          inputTokens
          outputTokens
          errors
        }
        memoryExtractedAt
        memoryExtractionModelId
        memoryExtractionMetadata {
          extractedCount
          userCount
          familyCount
          prefCount
          todoCount
          inputTokens
          outputTokens
          errors
        }
      }
      messages {
        id
        threadId
        turnId
        sequence
        role
        payload
        modelId
        errorMessage
        startedAt
        completedAt
        latencyMs
        thinkingMs
        firstTextAt
        ttftMs
        createdAt
      }
    }
  }
`;

const ADMIN_DELETE_SESSION = gql`
  mutation AdminDeleteAssistantSession($threadId: ID!, $reason: String!) {
    adminDeleteAssistantSession(threadId: $threadId, reason: $reason)
  }
`;

const ADMIN_DELETE_ALL_SESSIONS = gql`
  mutation AdminDeleteAllAssistantSessionsForUser($userId: ID!, $reason: String!) {
    adminDeleteAllAssistantSessionsForUser(userId: $userId, reason: $reason)
  }
`;

const ADMIN_RECOMPACT_SESSION = gql`
  mutation AdminRecompactAssistantSession($threadId: ID!) {
    adminRecompactAssistantSession(threadId: $threadId)
  }
`;

const ADMIN_REEXTRACT_SESSION = gql`
  mutation AdminReExtractAssistantSessionMemory($threadId: ID!) {
    adminReExtractAssistantSessionMemory(threadId: $threadId)
  }
`;

const ADMIN_LIST_FEEDBACK_FOR_THREAD = gql`
  query AdminListAssistantFeedbackForThread($threadId: ID!) {
    adminListAssistantFeedbackForThread(threadId: $threadId) {
      id
      userId
      threadId
      turnId
      body
      submitterEmail
      createdAt
    }
  }
`;

type SessionRow = AdminListAssistantSessionsForUserQuery['adminListAssistantSessionsForUser']['sessions'][number];

// Stable empty reference so turns without feedback don't get a fresh array each
// render (keeps TurnGroup's props referentially stable).
const EMPTY_FEEDBACK: FeedbackRow[] = [];

const CHANNEL_BADGE: Record<string, string> = {
  app: 'border-border-primary text-fg-secondary',
  sms: 'border-border-brand text-fg-brand-primary',
  email: 'border-fg-warning-primary text-fg-warning-primary',
};

const STATUS_BADGE: Record<string, string> = {
  active: 'border-border-primary text-fg-primary',
  archived: 'border-border-primary text-fg-tertiary',
};

/**
 * Sessions sub-tab of the Assistant tool. Operator picks a user; the
 * pane loads every thread (active + archived) for that user, ordered by
 * last-activity desc. Click a thread on the left to see its messages on
 * the right — each rendered as a JSON-style card colored by role, with
 * user / assistant prose bolded and brought to the front. Per-row delete
 * + bulk-delete-all-sessions are gated behind typed-reason modals;
 * deletes are hard via FK cascade (no soft-delete column).
 */
const AssistantSessionsScene = () => {
  // Deep-link params (set when arriving from the Feedback tab). Read once for
  // the initial state; `highlightTurnId` is also read every render to badge +
  // scroll the targeted turn.
  const [searchParams] = useSearchParams();
  const linkUserId = searchParams.get('userId');
  const linkFamilyId = searchParams.get('familyId');
  const linkThreadId = searchParams.get('threadId');
  const highlightTurnId = searchParams.get('turnId');

  const [selection, setSelection] = React.useState<IAdminSelectUserSelection | null>(
    linkUserId && linkFamilyId ? { userId: linkUserId, familyId: linkFamilyId } : null
  );
  const [selectedThreadId, setSelectedThreadId] = React.useState<string | null>(linkThreadId);
  const [deleteThreadConfirm, setDeleteThreadConfirm] = React.useState<SessionRow | null>(null);
  const [deleteReason, setDeleteReason] = React.useState('');
  const [deleteAllOpen, setDeleteAllOpen] = React.useState(false);
  const [deleteAllReason, setDeleteAllReason] = React.useState('');
  const [actionLoading, setActionLoading] = React.useState(false);
  // Thread whose re-compaction is in flight. Latched on click so the link
  // becomes a non-interactive "processing…" label (prevents double-fire).
  // Backend is fire-and-forget with no completion signal, so it stays latched
  // until the operator switches threads (cleared in the detail-load effect) or
  // refreshes — the toast tells them to refresh to see the result.
  const [recompactingThreadId, setRecompactingThreadId] = React.useState<string | null>(null);
  // Thread whose memory re-extraction is in flight — same latch contract as
  // `recompactingThreadId`.
  const [reextractingThreadId, setReextractingThreadId] = React.useState<string | null>(null);
  // Latches once we auto-pick the first session for a given user. Reset
  // on user switch so the next user gets their own auto-pick, but
  // unchanged on delete-then-refetch — operators who just confirmed a
  // delete shouldn't have a sibling session jump into the detail pane
  // uninvited.
  // Skip the first-session auto-pick when we arrived with a deep-linked thread —
  // the operator wants that exact thread, not sessions[0].
  const hasAutoSelectedRef = React.useRef(linkThreadId !== null);

  const { showToast } = useToast();
  const { user: mopsUser } = useAuthState();

  const userId = selection?.userId ?? null;

  const [loadSessions, { data: sessionsData, loading: sessionsLoading, refetch: refetchSessions }] = useLazyQuery<
    AdminListAssistantSessionsForUserQuery,
    AdminListAssistantSessionsForUserQueryVariables
  >(ADMIN_LIST_SESSIONS, { fetchPolicy: 'cache-and-network', notifyOnNetworkStatusChange: true });

  const [loadSessionDetail, { data: detailData, loading: detailLoading, fetchMore: fetchMoreMessages }] = useLazyQuery<
    AdminGetAssistantSessionDetailQuery,
    AdminGetAssistantSessionDetailQueryVariables
  >(ADMIN_GET_SESSION_DETAIL, { fetchPolicy: 'cache-and-network', notifyOnNetworkStatusChange: true });

  const [loadFeedback, { data: feedbackData }] = useLazyQuery<
    AdminListAssistantFeedbackForThreadQuery,
    AdminListAssistantFeedbackForThreadQueryVariables
  >(ADMIN_LIST_FEEDBACK_FOR_THREAD, { fetchPolicy: 'cache-and-network' });

  const [deleteSession] = useMutation<
    AdminDeleteAssistantSessionMutation,
    AdminDeleteAssistantSessionMutationVariables
  >(ADMIN_DELETE_SESSION);

  const [deleteAllSessions] = useMutation<
    AdminDeleteAllAssistantSessionsForUserMutation,
    AdminDeleteAllAssistantSessionsForUserMutationVariables
  >(ADMIN_DELETE_ALL_SESSIONS);

  const [recompactSession] = useMutation<
    AdminRecompactAssistantSessionMutation,
    AdminRecompactAssistantSessionMutationVariables
  >(ADMIN_RECOMPACT_SESSION);

  const [reextractSession] = useMutation<
    AdminReExtractAssistantSessionMemoryMutation,
    AdminReExtractAssistantSessionMemoryMutationVariables
  >(ADMIN_REEXTRACT_SESSION);

  React.useEffect(() => {
    if (userId) {
      void loadSessions({ variables: { userId } });
    }
  }, [userId, loadSessions]);

  React.useEffect(() => {
    setRecompactingThreadId(null);
    setReextractingThreadId(null);
    if (selectedThreadId) {
      void loadSessionDetail({ variables: { threadId: selectedThreadId, afterSequence: null } });
      void loadFeedback({ variables: { threadId: selectedThreadId } });
    }
  }, [selectedThreadId, loadSessionDetail, loadFeedback]);

  const handleUserSelect = (next: IAdminSelectUserSelection | null) => {
    setSelection(next);
    setSelectedThreadId(null);
    setDeleteThreadConfirm(null);
    setDeleteReason('');
    setDeleteAllOpen(false);
    setDeleteAllReason('');
    hasAutoSelectedRef.current = false;
  };

  const sessions = React.useMemo(() => sessionsData?.adminListAssistantSessionsForUser?.sessions ?? [], [sessionsData]);
  const sessionsHasMore = sessionsData?.adminListAssistantSessionsForUser?.hasMore ?? false;
  const detail = detailData?.adminGetAssistantSessionDetail ?? null;

  // Feedback for the selected thread, grouped by the turn it targets. Filtered
  // to the active thread so a stale response from a just-switched thread can't
  // bleed badges onto the wrong turns.
  const feedbackByTurnId = React.useMemo(() => {
    const map = new Map<string, FeedbackRow[]>();
    for (const fb of feedbackData?.adminListAssistantFeedbackForThread ?? []) {
      if (fb.threadId !== selectedThreadId) {
        continue;
      }
      const list = map.get(fb.turnId) ?? [];
      list.push(fb);
      map.set(fb.turnId, list);
    }
    return map;
  }, [feedbackData, selectedThreadId]);

  // Auto-pick the first session once per user selection so the operator
  // lands on real data instead of a blank pane. The ref guard prevents
  // a post-delete refetch from quietly jumping the operator to a sibling
  // session — `selectedThreadId === null` after a delete looks identical
  // to "nothing loaded yet" without it.
  React.useEffect(() => {
    if (!hasAutoSelectedRef.current && selectedThreadId === null && sessions.length > 0) {
      hasAutoSelectedRef.current = true;
      setSelectedThreadId(sessions[0].id);
    }
  }, [sessions, selectedThreadId]);

  // Resolver paginates forward in `sequence ASC` (the orchestrator's
  // reconnect-replay shape); each call adds messages that came after the
  // ones we already have, so the affordance reads "Load more" rather
  // than "older" / "newer" to stay direction-honest. Whether the pane
  // should flip to chat-style "newest first" UX is tracked separately.
  const handleLoadMoreMessages = async () => {
    if (!detail || !detail.hasMore || detail.nextAfterSequence === null || detail.nextAfterSequence === undefined) {
      return;
    }
    await fetchMoreMessages({
      variables: { threadId: detail.session.id, afterSequence: detail.nextAfterSequence },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult) {
          return prev;
        }
        return {
          ...fetchMoreResult,
          adminGetAssistantSessionDetail: {
            ...fetchMoreResult.adminGetAssistantSessionDetail,
            messages: [
              ...prev.adminGetAssistantSessionDetail.messages,
              ...fetchMoreResult.adminGetAssistantSessionDetail.messages,
            ],
          },
        };
      },
    });
  };

  const handleConfirmDeleteSession = async () => {
    if (!deleteThreadConfirm || deleteReason.trim().length === 0) {
      return;
    }
    setActionLoading(true);
    try {
      await deleteSession({
        variables: {
          threadId: deleteThreadConfirm.id,
          reason: prefixReason(deleteReason, mopsUser?.email ?? null),
        },
      });
      showToast({ type: 'success', message: 'Session deleted.' });
      const wasSelected = selectedThreadId === deleteThreadConfirm.id;
      setDeleteThreadConfirm(null);
      setDeleteReason('');
      if (wasSelected) {
        setSelectedThreadId(null);
      }
      await refetchSessions();
    } catch {
      // Apollo errorLink reports to Sentry.
    } finally {
      setActionLoading(false);
    }
  };

  // Force a fresh compaction run on an archived thread so we can eyeball the
  // result on real data. Fire-and-forget on the backend (the `@orn/api` worker
  // returns 202 and runs the LLM call in the background), so there's no
  // deterministic completion signal — the toast tells the operator to refresh.
  const handleRecompactSession = async (threadId: string) => {
    setRecompactingThreadId(threadId);
    try {
      await recompactSession({ variables: { threadId } });
      showToast({
        type: 'info',
        message: 'Re-compaction queued — it will finish shortly. Refresh in a few seconds to see the result.',
      });
      // Stays latched as "processing…" — there's no completion signal for the
      // background compaction; switching threads or refreshing clears it.
    } catch {
      // Apollo errorLink reports to Sentry; a 404/409 from the worker surfaces
      // as a GraphQL error here. Unlatch so the operator can retry.
      setRecompactingThreadId(null);
    }
  };

  // Re-run memory extraction on an archived thread: the worker deletes the
  // thread's prior extractor memories and re-mines them. Same fire-and-forget
  // latch contract as re-compaction. A 409 (non-archived, or memory disabled
  // for the user) surfaces as a GraphQL error → caught here, unlatched.
  const handleReextractSession = async (threadId: string) => {
    setReextractingThreadId(threadId);
    try {
      await reextractSession({ variables: { threadId } });
      showToast({
        type: 'info',
        message: 'Re-extraction queued — it will finish shortly. Refresh in a few seconds to see the result.',
      });
    } catch {
      setReextractingThreadId(null);
    }
  };

  const handleConfirmDeleteAll = async () => {
    if (!userId || deleteAllReason.trim().length === 0) {
      return;
    }
    setActionLoading(true);
    try {
      const result = await deleteAllSessions({
        variables: { userId, reason: prefixReason(deleteAllReason, mopsUser?.email ?? null) },
      });
      const count = result.data?.adminDeleteAllAssistantSessionsForUser ?? 0;
      showToast({
        type: 'success',
        message: count === 0 ? 'No sessions to delete.' : `Deleted ${count} sessions.`,
      });
      setDeleteAllOpen(false);
      setDeleteAllReason('');
      setSelectedThreadId(null);
      await refetchSessions();
    } catch {
      // Apollo errorLink reports to Sentry.
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <>
      <div className="flex items-start justify-between gap-3">
        <div className="w-full max-w-md">
          <AdminSelectUser selectedUserId={selection?.userId ?? null} onSelectionChange={handleUserSelect} />
        </div>
        {userId && sessions.length > 0 && (
          <Button color="danger" size="sm" isDisabled={actionLoading} onClick={() => setDeleteAllOpen(true)}>
            Delete all sessions
          </Button>
        )}
      </div>

      {!userId ? (
        <div className="text-fg-tertiary text-xs italic">Select a user to view their assistant sessions.</div>
      ) : sessionsLoading && sessions.length === 0 ? (
        <div className="text-fg-secondary text-xs">Loading sessions…</div>
      ) : sessions.length === 0 ? (
        <div className="text-fg-tertiary text-xs italic">No assistant sessions for this user.</div>
      ) : (
        <div className="border-border-primary flex min-h-0 flex-1 border">
          <SessionList
            sessions={sessions}
            selectedThreadId={selectedThreadId}
            onSelect={setSelectedThreadId}
            hasMore={sessionsHasMore}
          />
          <div className="flex-1 overflow-y-auto">
            {selectedThreadId === null ? (
              <div className="text-fg-tertiary p-6 text-xs italic">Pick a session from the left to inspect it.</div>
            ) : detailLoading && detail === null ? (
              <div className="text-fg-secondary p-6 text-xs">Loading messages…</div>
            ) : detail === null ? (
              <div className="text-fg-tertiary p-6 text-xs italic">Session detail unavailable.</div>
            ) : (
              <SessionDetail
                detail={detail}
                actionLoading={actionLoading}
                onDelete={() => {
                  setDeleteThreadConfirm(detail.session);
                  setDeleteReason('');
                }}
                onRecompact={() => void handleRecompactSession(detail.session.id)}
                isRecompacting={recompactingThreadId === detail.session.id}
                onReextract={() => void handleReextractSession(detail.session.id)}
                isReextracting={reextractingThreadId === detail.session.id}
                onLoadMore={() => void handleLoadMoreMessages()}
                onJumpToPrior={(priorId) => {
                  if (sessions.some((s) => s.id === priorId)) {
                    setSelectedThreadId(priorId);
                  }
                }}
                sessionsInList={sessions}
                feedbackByTurnId={feedbackByTurnId}
                highlightTurnId={highlightTurnId}
              />
            )}
          </div>
        </div>
      )}

      <Modal
        isOpen={deleteThreadConfirm !== null}
        onOpenChange={(open) => !open && setDeleteThreadConfirm(null)}
        size="md"
      >
        <Modal.Header onClose={() => setDeleteThreadConfirm(null)}>Delete session</Modal.Header>
        <Modal.Body>
          <div className="flex flex-col gap-4">
            <div className="text-fg-primary text-sm">
              Hard-deletes this thread and all its messages — irreversible. If the user has an active WebSocket
              conversation in this thread right now, the in-flight turn may be lost. Reason is recorded in operational
              logs.
            </div>
            {deleteThreadConfirm && (
              <div className="text-fg-secondary border-border-primary border px-3 py-2 text-xs">
                <div className="font-mono text-[11px]">
                  {deleteThreadConfirm.id} · {deleteThreadConfirm.messageCount} msg · {deleteThreadConfirm.channel} ·{' '}
                  {deleteThreadConfirm.status}
                </div>
              </div>
            )}
            <Input
              label="Reason"
              placeholder="e.g. user privacy request, support ticket #1234"
              value={deleteReason}
              onChange={setDeleteReason}
            />
            <div className="flex justify-end gap-2">
              <Button color="secondary" size="sm" onClick={() => setDeleteThreadConfirm(null)}>
                Cancel
              </Button>
              <Button
                color="danger"
                size="sm"
                isDisabled={deleteReason.trim().length === 0 || actionLoading}
                isLoading={actionLoading}
                onClick={() => void handleConfirmDeleteSession()}
              >
                Delete session
              </Button>
            </div>
          </div>
        </Modal.Body>
      </Modal>

      <Modal isOpen={deleteAllOpen} onOpenChange={(open) => !open && setDeleteAllOpen(false)} size="md">
        <Modal.Header onClose={() => setDeleteAllOpen(false)}>Delete all sessions for user</Modal.Header>
        <Modal.Body>
          <div className="flex flex-col gap-4">
            <div className="text-fg-primary text-sm">
              Hard-deletes <span className="font-semibold">every</span> assistant thread (active and archived) and all
              messages for this user — irreversible. If the user has an active WebSocket conversation, the in-flight
              turn may be lost. Reason is recorded in operational logs.
            </div>
            <div className="text-fg-secondary border-border-primary border px-3 py-2 text-xs">
              <span className="font-mono text-[11px]">
                {sessions.length} session{sessions.length === 1 ? '' : 's'} on record
              </span>
            </div>
            <Input
              label="Reason"
              placeholder="e.g. account deletion / GDPR request"
              value={deleteAllReason}
              onChange={setDeleteAllReason}
            />
            <div className="flex justify-end gap-2">
              <Button color="secondary" size="sm" onClick={() => setDeleteAllOpen(false)}>
                Cancel
              </Button>
              <Button
                color="danger"
                size="sm"
                isDisabled={deleteAllReason.trim().length === 0 || actionLoading}
                isLoading={actionLoading}
                onClick={() => void handleConfirmDeleteAll()}
              >
                Delete all
              </Button>
            </div>
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
};

interface ISessionListProps {
  sessions: SessionRow[];
  selectedThreadId: string | null;
  onSelect: (threadId: string) => void;
  hasMore: boolean;
}

const SessionList = ({ sessions, selectedThreadId, onSelect, hasMore }: ISessionListProps) => (
  <aside className="border-border-primary w-72 shrink-0 overflow-y-auto border-r">
    {hasMore && (
      <div className="border-border-primary text-fg-warning-primary border-b px-3 py-2 text-[11px]">
        Showing 100 most recent · older sessions hidden
      </div>
    )}
    <ul>
      {sessions.map((session) => {
        const isSelected = session.id === selectedThreadId;
        return (
          <li key={session.id}>
            <button
              type="button"
              onClick={() => onSelect(session.id)}
              className={cx(
                'border-border-primary flex w-full flex-col gap-1 border-b px-3 py-2 text-left transition-colors',
                isSelected
                  ? 'bg-bg-secondary text-fg-primary border-l-fg-brand-primary border-l-2 pl-[10px]'
                  : 'hover:bg-bg-secondary text-fg-secondary'
              )}
            >
              <div className="flex items-baseline justify-between gap-2">
                <span className="text-[11px] font-medium">{formatTimeAgo(session.lastActivityAt)}</span>
                <span className="text-fg-quaternary font-mono text-[10px]">{session.id.slice(-8)}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <span
                  className={cx(
                    'inline-flex items-center border px-1.5 py-px text-[9px] font-medium uppercase tracking-wider',
                    CHANNEL_BADGE[session.channel] ?? CHANNEL_BADGE.app
                  )}
                >
                  {session.channel}
                </span>
                <span
                  className={cx(
                    'inline-flex items-center border px-1.5 py-px text-[9px] font-medium uppercase tracking-wider',
                    STATUS_BADGE[session.status] ?? STATUS_BADGE.active
                  )}
                >
                  {session.status}
                </span>
                <span className="text-fg-tertiary font-mono text-[10px]">{session.messageCount} msg</span>
              </div>
            </button>
          </li>
        );
      })}
    </ul>
  </aside>
);

type SessionDetailRow = NonNullable<AdminGetAssistantSessionDetailQuery['adminGetAssistantSessionDetail']>['session'];

interface ICompactionHeaderRowsProps {
  session: SessionDetailRow;
}

/**
 * Compaction stats as `<dl>` rows for the thread header. Renders inside
 * the parent's grid (so it must emit bare `<dt>`/`<dd>` pairs, not a
 * wrapper). Shows the per-run counts + token spend once `compactedAt` is
 * set; a single "not compacted" row until then.
 */
const CompactionHeaderRows = ({ session }: ICompactionHeaderRowsProps) => {
  if (session.compactedAt === null || session.compactedAt === undefined) {
    return (
      <>
        <dt className="text-fg-tertiary">compaction</dt>
        <dd className="text-fg-tertiary italic">not compacted</dd>
      </>
    );
  }
  const m = session.compactionMetadata;
  return (
    <>
      <dt className="text-fg-tertiary">compacted</dt>
      <dd className="font-mono">{formatTimeAgo(session.compactedAt)}</dd>
      <dt className="text-fg-tertiary pl-3">model</dt>
      <dd className="font-mono">{session.compactionModelId ?? '—'}</dd>
      {m !== null && m !== undefined && (
        <>
          <dt className="text-fg-tertiary pl-3" title="kept / summarized / dropped messages">
            kept/summ/drop
          </dt>
          <dd className="font-mono tabular-nums">
            {m.keptCount} / {m.summarizedCount} / {m.droppedCount}
          </dd>
          <dt className="text-fg-tertiary pl-3">tokens</dt>
          <dd className="font-mono tabular-nums">
            {m.inputTokens} in / {m.outputTokens} out
          </dd>
          {m.errors !== null && m.errors !== undefined && m.errors.length > 0 && (
            <>
              <dt className="text-fg-tertiary pl-3">errors</dt>
              <dd className="text-fg-error-primary font-mono tabular-nums" title={m.errors.join('\n')}>
                {m.errors.length}
              </dd>
            </>
          )}
        </>
      )}
    </>
  );
};

/**
 * Memory-extraction stats as `<dl>` rows for the thread header — the backup
 * pass that mines durable memories the agent's in-session tool missed. Same
 * bare-`<dt>`/`<dd>` contract as {@link CompactionHeaderRows}; a single "not
 * extracted" row until `memoryExtractedAt` is set.
 */
const MemoryExtractionHeaderRows = ({ session }: ICompactionHeaderRowsProps) => {
  if (session.memoryExtractedAt === null || session.memoryExtractedAt === undefined) {
    return (
      <>
        <dt className="text-fg-tertiary">extraction</dt>
        <dd className="text-fg-tertiary italic">not extracted</dd>
      </>
    );
  }
  const m = session.memoryExtractionMetadata;
  return (
    <>
      <dt className="text-fg-tertiary">extracted</dt>
      <dd className="font-mono">{formatTimeAgo(session.memoryExtractedAt)}</dd>
      <dt className="text-fg-tertiary pl-3">model</dt>
      <dd className="font-mono">{session.memoryExtractionModelId ?? '—'}</dd>
      {m !== null && m !== undefined && (
        <>
          <dt className="text-fg-tertiary pl-3" title="total mined (user / family · pref / todo)">
            mined
          </dt>
          <dd className="font-mono tabular-nums">
            {m.extractedCount} ({m.userCount} usr / {m.familyCount} fam · {m.prefCount} pref / {m.todoCount} todo)
          </dd>
          <dt className="text-fg-tertiary pl-3">tokens</dt>
          <dd className="font-mono tabular-nums">
            {m.inputTokens} in / {m.outputTokens} out
          </dd>
          {m.errors !== null && m.errors !== undefined && m.errors.length > 0 && (
            <>
              <dt className="text-fg-tertiary pl-3">errors</dt>
              <dd className="text-fg-error-primary font-mono tabular-nums" title={m.errors.join('\n')}>
                {m.errors.length}
              </dd>
            </>
          )}
        </>
      )}
    </>
  );
};

interface ICompactionSummaryPanelProps {
  summary: string;
}

/**
 * The compaction summary, collapsed to a one-line preview that expands to
 * the full narrative on click. Mirrors `CollapsibleMessageCard`'s header
 * affordance (chevron + uppercase label + truncated preview). Only
 * rendered when the thread carries a non-empty summary.
 */
const CompactionSummaryPanel = ({ summary }: ICompactionSummaryPanelProps) => {
  const [expanded, setExpanded] = React.useState(false);
  const preview = firstLineOfSummary(summary);
  return (
    <div className="border-border-primary border-b pb-3">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
        className="hover:bg-bg-secondary -ml-1 flex w-full items-center gap-2 px-1 py-0.5 text-left font-mono text-[10px] uppercase"
      >
        <span className="text-fg-quaternary shrink-0">{expanded ? '▾' : '▸'}</span>
        <span className="text-fg-brand-primary shrink-0">summary</span>
        {!expanded && <span className="text-fg-secondary min-w-0 flex-1 truncate normal-case">{preview}</span>}
      </button>
      {expanded && (
        <div className="text-fg-primary whitespace-pre-wrap px-1 pt-2 font-mono text-[11px] leading-snug">
          {summary}
        </div>
      )}
    </div>
  );
};

interface ISessionDetailProps {
  detail: NonNullable<AdminGetAssistantSessionDetailQuery['adminGetAssistantSessionDetail']>;
  actionLoading: boolean;
  onDelete: () => void;
  onRecompact: () => void;
  isRecompacting: boolean;
  onReextract: () => void;
  isReextracting: boolean;
  onLoadMore: () => void;
  onJumpToPrior: (priorThreadId: string) => void;
  sessionsInList: SessionRow[];
  feedbackByTurnId: Map<string, FeedbackRow[]>;
  /** turnId to scroll to + highlight when arriving from a Feedback-tab deep link. */
  highlightTurnId: string | null;
}

const SessionDetail = ({
  detail,
  actionLoading,
  onDelete,
  onRecompact,
  isRecompacting,
  onReextract,
  isReextracting,
  onLoadMore,
  onJumpToPrior,
  sessionsInList,
  feedbackByTurnId,
  highlightTurnId,
}: ISessionDetailProps) => {
  const { session, messages, hasMore } = detail;
  const priorIsInList = session.priorThreadId !== null && sessionsInList.some((s) => s.id === session.priorThreadId);
  return (
    <div className="flex flex-col gap-3 p-4">
      <div className="border-border-primary flex items-start justify-between gap-3 border-b pb-3">
        <dl className="text-fg-secondary grid grid-cols-2 gap-x-4 gap-y-1 text-[11px]">
          <dt className="text-fg-tertiary">id</dt>
          <dd className="text-fg-primary font-mono">{session.id}</dd>
          <dt className="text-fg-tertiary">channel</dt>
          <dd>{session.channel}</dd>
          <dt className="text-fg-tertiary">status</dt>
          <dd>{session.status}</dd>
          <dt className="text-fg-tertiary">messages</dt>
          <dd>{session.messageCount}</dd>
          <dt className="text-fg-tertiary">created</dt>
          <dd className="font-mono">{formatInstantInTimeZone(session.createdAt, PACIFIC_TIME_ZONE)}</dd>
          <dt className="text-fg-tertiary">last activity</dt>
          <dd className="font-mono">{formatTimeAgo(session.lastActivityAt)}</dd>
          {session.priorThreadId && (
            <>
              <dt className="text-fg-tertiary">prior</dt>
              <dd className="font-mono">
                {priorIsInList ? (
                  <button
                    type="button"
                    onClick={() => session.priorThreadId && onJumpToPrior(session.priorThreadId)}
                    className="text-fg-brand-primary hover:underline"
                  >
                    {session.priorThreadId.slice(-8)}
                  </button>
                ) : (
                  <span className="text-fg-tertiary" title={session.priorThreadId}>
                    {session.priorThreadId.slice(-8)} (not loaded)
                  </span>
                )}
              </dd>
            </>
          )}
          <CompactionHeaderRows session={session} />
          <dt className="text-fg-tertiary pl-3">re-compact</dt>
          <dd>
            {isRecompacting ? (
              <span className="text-fg-quaternary italic">processing…</span>
            ) : (
              <button
                type="button"
                onClick={onRecompact}
                disabled={actionLoading || session.status !== 'archived'}
                title={session.status === 'archived' ? undefined : 'Only archived sessions can be re-compacted'}
                className="text-fg-brand-primary disabled:text-fg-quaternary hover:underline disabled:cursor-not-allowed disabled:no-underline"
              >
                re-compact now
              </button>
            )}
          </dd>
          <MemoryExtractionHeaderRows session={session} />
          <dt className="text-fg-tertiary pl-3">re-extract</dt>
          <dd>
            {isReextracting ? (
              <span className="text-fg-quaternary italic">processing…</span>
            ) : (
              <button
                type="button"
                onClick={onReextract}
                disabled={actionLoading || session.status !== 'archived'}
                title={session.status === 'archived' ? undefined : 'Only archived sessions can be re-extracted'}
                className="text-fg-brand-primary disabled:text-fg-quaternary hover:underline disabled:cursor-not-allowed disabled:no-underline"
              >
                re-extract now
              </button>
            )}
          </dd>
        </dl>
        <Button color="danger" size="sm" isDisabled={actionLoading} onClick={onDelete}>
          Delete session
        </Button>
      </div>

      {session.summary !== null && session.summary !== undefined && session.summary.length > 0 && (
        <CompactionSummaryPanel summary={session.summary} />
      )}

      {messages.length === 0 ? (
        <div className="text-fg-tertiary text-xs italic">No messages persisted for this thread.</div>
      ) : (
        <div className="flex flex-col">
          {groupMessagesByTurn(messages).map((group) => (
            <TurnGroup
              key={group.turnId}
              group={group}
              feedback={feedbackByTurnId.get(group.turnId) ?? EMPTY_FEEDBACK}
              highlight={highlightTurnId === group.turnId}
            />
          ))}
          {hasMore && (
            <div className="flex justify-center py-3">
              <Button color="secondary" size="sm" isDisabled={actionLoading} onClick={onLoadMore}>
                Load more messages
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AssistantSessionsScene;
